import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GraduationCap, ChevronRight, AlertTriangle, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useReportTileEmpty } from "./DashboardTile";

interface TrainingDueItem {
  assignmentId: number;
  moduleTitle: string;
  dueDate: string | null;
  progress: number;
  isOverdue: boolean;
}

interface TrainingDueWidgetProps {
  items?: TrainingDueItem[];
}

export default function TrainingDueWidget({ items: propItems }: TrainingDueWidgetProps) {
  const [, navigate] = useLocation();

  // Fallback: fetch from training assignments if not provided
  const { data: fetchedItems, isLoading } = useQuery<any[]>({
    queryKey: ["/api/training/my-assignments"],
    enabled: !propItems,
    select: (data) => {
      if (!Array.isArray(data)) return [];
      return data
        .filter((a: any) => a.status !== 'completed')
        .slice(0, 5)
        .map((a: any) => ({
          assignmentId: a.id,
          moduleTitle: a.module?.title || a.title || 'Training',
          dueDate: a.dueDate || null,
          progress: a.progress ?? 0,
          isOverdue: a.dueDate ? new Date(a.dueDate) < new Date() : false,
        }));
    },
  });

  const items = propItems ?? fetchedItems ?? [];

  useReportTileEmpty("training_due", (propItems != null || !isLoading) && (items?.length ?? 0) === 0);

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 shadow-md">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">Training Due</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950/30 gap-1 font-semibold h-8 px-2"
            onClick={() => navigate("/training")}
          >
            View All <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {items.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No pending training</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.slice(0, 5).map((item) => (
              <div
                key={item.assignmentId}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-xl",
                  item.isOverdue ? "bg-rose-50 dark:bg-rose-950/30" : "bg-muted/50"
                )}
              >
                <div className={cn(
                  "p-1.5 rounded-lg mt-0.5",
                  item.isOverdue ? "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400" : "bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
                )}>
                  {item.isOverdue ? <AlertTriangle className="h-3.5 w-3.5" /> : <BookOpen className="h-3.5 w-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{item.moduleTitle}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {item.dueDate && (
                      <span className={cn(
                        "text-xs",
                        item.isOverdue ? "text-rose-600 dark:text-rose-400 font-medium" : "text-muted-foreground"
                      )}>
                        {item.isOverdue ? 'Overdue' : `Due ${formatDistanceToNow(new Date(item.dueDate), { addSuffix: true })}`}
                      </span>
                    )}
                    {item.progress > 0 && (
                      <span className="text-xs text-muted-foreground">{item.progress}% done</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
